import { useState, useRef, useEffect, useCallback } from 'react';

interface CinematicIntroModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

export const CINESYNC_INTRO_SEEN_KEY = 'cinesync_intro_seen';

export function CinematicIntroModal({
  isOpen,
  onComplete,
}: CinematicIntroModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isFadingOut, setIsFadingOut] = useState(false);

  const handleFinish = useCallback(() => {
    setIsFadingOut(true);
    setTimeout(() => {
      try {
        localStorage.setItem(CINESYNC_INTRO_SEEN_KEY, 'true');
      } catch {}
      onComplete();
    }, 600); // Smooth 600ms cinematic crossfade to registration
  }, [onComplete]);

  useEffect(() => {
    if (!isOpen) return;

    const video = videoRef.current;
    if (!video) return;

    setIsFadingOut(false);
    video.currentTime = 0;
    video.volume = 1.0;
    video.muted = false; // Primary goal: automatically play with sound

    // Attempt unmuted autoplay immediately
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // If mobile OS blocks unmuted autoplay without prior touch,
        // start playback muted so the visual entrance begins immediately,
        // and unmute on the very first screen touch anywhere
        video.muted = true;
        video.play().catch(() => {});

        const unmuteOnFirstTouch = () => {
          if (videoRef.current) {
            videoRef.current.muted = false;
            videoRef.current.volume = 1.0;
            if (videoRef.current.paused) {
              videoRef.current.play().catch(() => {});
            }
          }
        };

        window.addEventListener('touchstart', unmuteOnFirstTouch, { once: true, passive: true });
        window.addEventListener('click', unmuteOnFirstTouch, { once: true });
        window.addEventListener('pointerdown', unmuteOnFirstTouch, { once: true });
        window.addEventListener('keydown', unmuteOnFirstTouch, { once: true });
      });
    }

    // Global listener: ensure sound is unmuted on any user touch during the intro
    const ensureSound = () => {
      if (videoRef.current && videoRef.current.muted) {
        videoRef.current.muted = false;
        videoRef.current.volume = 1.0;
      }
    };
    window.addEventListener('touchstart', ensureSound, { passive: true });
    window.addEventListener('click', ensureSound);
    window.addEventListener('pointerdown', ensureSound);

    return () => {
      window.removeEventListener('touchstart', ensureSound);
      window.removeEventListener('click', ensureSound);
      window.removeEventListener('pointerdown', ensureSound);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="CineSync Entrance"
      className={`fixed inset-0 z-[99999] w-screen h-screen bg-black overflow-hidden flex items-center justify-center select-none transition-opacity duration-700 cursor-default ${
        isFadingOut ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    >
      <video
        ref={videoRef}
        src="/cinesync_intro.mp4"
        autoPlay
        playsInline
        // @ts-ignore
        webkit-playsinline="true"
        preload="auto"
        onEnded={handleFinish}
        className="w-full h-full object-cover sm:object-contain bg-black pointer-events-none"
      />
    </div>
  );
}
